import CloseIcon from "@mui/icons-material/Close";
import {LoadingButton} from "@mui/lab";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, useMediaQuery} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {useNavigate} from "react-router-dom";
import InfoTooltip from "../../common/InfoTooltip";
import TemplatePicker, {BLANK_TEMPLATE_KEY} from "../../common/pickers/TemplatePicker";
import DigitalSigningOfferField from "./DigitalSigningOfferField";
import {DIGITAL_SIGNING_TEMPLATES_PATH, DigitalSigningTemplate, DigitalSigningTemplateStarter} from "./digitalSigningTemplate.model";
import {DigitalSigningTemplateService} from "./digitalSigningTemplate.service";
import {completeTipSettings, designOf} from "./digitalSigningTemplate.util";
import {invalidateDigitalSigningTemplateChoices} from "../digitalSigningQueries";

interface Props {
    open: boolean
    onClose: () => void
}

const TITLE_ID = 'create-digital-signing-template-dialog-title';

// A dialog field's label with its hint; the tip service dialog's too
export const DialogField = ({label, hint, children}: {label: string, hint: string, children: React.ReactNode}) => (
    <Stack gap={0.75}>
        <Stack direction={'row'} alignItems={'center'}>
            <Typography variant={'body2'} fontWeight={500}>
                {label}
            </Typography>
            <InfoTooltip title={hint}/>
        </Stack>
        {children}
    </Stack>
);

const DigitalSigningTemplateCreateDialog = ({open, onClose}: Props) => {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const readOnly = useReadOnly();
    const queryClient = useQueryClient();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [starters, setStarters] = useState<DigitalSigningTemplateStarter[]>([]);
    const [blankDraft, setBlankDraft] = useState<DigitalSigningTemplate>();
    const [selectedKey, setSelectedKey] = useState(BLANK_TEMPLATE_KEY);
    const [nameIsSuggestion, setNameIsSuggestion] = useState(true);

    const data = useEntity<DigitalSigningTemplate>({
        get: async () => {
            const [catalog, blank] = await Promise.all([
                DigitalSigningTemplateService.starters(i18n.language),
                DigitalSigningTemplateService.blank(),
            ]);
            setStarters(catalog);
            setBlankDraft(blank);
            return {...blank, name: t('New template')};
        },
        // Starters instantiate from template ID; blank templates are created via add().
        create: draft => {
            const starter = starters.find(entry => entry.id === selectedKey);
            return starter
                ? DigitalSigningTemplateService.instantiate({
                    templateId: starter.id,
                    language: i18n.language,
                    name: draft.name,
                    scopedSectorIds: draft.scopedSectorIds,
                })
                : DigitalSigningTemplateService.add({
                    name: draft.name ?? '',
                    scopedSectorIds: draft.scopedSectorIds ?? [],
                    tipSettings: completeTipSettings(draft.tipSettings),
                    design: designOf(draft),
                    privacyPolicyUrl: draft.privacyPolicyUrl ?? '',
                    imprintUrl: draft.imprintUrl ?? '',
                    thankYouMessages: draft.thankYouMessages ?? {},
                    titles: draft.titles ?? {},
                    subtitles: draft.subtitles ?? {},
                });
        },
        // useEntity requires both handlers even though this dialog only creates: a draft has no id.
        update: async template => template,
        // Only once the backend's draft has loaded: a name typed before it, or after it failed, has no tip settings to send
        isValid: draft => !!draft.name?.trim() && !!draft.tipSettings,
        cleanupBeforeSave: draft => ({...draft, name: draft.name?.trim() || undefined}),
        onSaved: template => {
            void invalidateDigitalSigningTemplateChoices(queryClient);
            onClose();
            navigate(`${DIGITAL_SIGNING_TEMPLATES_PATH}/${template.id}`);
        },
    }, 'new');

    const draft = data.item;

    const safeClose = () => {
        if (!data.saving) onClose();
    };

    const saveNow = () => {
        if (!data.isValid || readOnly || data.saving) return;
        data.setErrorSaving(false);
        void data.save();
    };

    const nameRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const timer = window.setTimeout(() => {
            nameRef.current?.focus();
            nameRef.current?.select();
        }, 250);
        return () => window.clearTimeout(timer);
    }, []);

    const nameMissing = !data.loading && !draft.name?.trim();

    const applyStarter = (key: string) => {
        const starter = starters.find(entry => entry.id === key);
        const base = starter?.draft ?? blankDraft;
        if (!base) return;

        const keepTyped = !!draft.name?.trim() && !nameIsSuggestion;
        const suggestion = starter ? starter.name : t('New template');

        setSelectedKey(key);
        setNameIsSuggestion(!keepTyped);
        data.setItem({...base, name: keepTyped ? draft.name : suggestion});
    };

    const starterOptions = useMemo(
        () => starters.map(starter => ({key: starter.id, name: starter.name, description: starter.description})),
        [starters],
    );

    return (
        <Dialog
            open={open}
            onClose={safeClose}
            maxWidth={'sm'}
            fullWidth
            fullScreen={fullScreen}
            aria-labelledby={TITLE_ID}
        >
            <DialogTitle component={'div'} id={`${TITLE_ID}-bar`} sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
                    <Typography component={'h2'} id={TITLE_ID} variant={'h6'} fontWeight={600} noWrap>
                        {t('Create template')}
                    </Typography>
                    <IconButton onClick={safeClose} size={'small'} aria-label={t('Close')}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{px: 3, py: 3}}>
                <Stack gap={3}>
                    <DialogField
                        label={`${t('Name')} *`}
                        hint={t('Staff see it when they create a link; the guest never does.')}
                    >
                        <TextField
                            id={'name'}
                            inputProps={{'aria-label': t('Name')}}
                            size={'small'}
                            inputRef={nameRef}
                            value={draft.name || ''}
                            onChange={event => {
                                const name = event.target.value;
                                setNameIsSuggestion(false);
                                data.setItem(previous => ({...previous, name}));
                            }}
                            onFocus={event => (event.target as HTMLInputElement).select()}
                            onKeyDown={event => {
                                if (event.key === 'Enter') saveNow();
                            }}
                            fullWidth
                            required
                            disabled={readOnly}
                            error={nameMissing}
                            // Reserve helper-text height to avoid a validation layout shift.
                            helperText={nameMissing ? t('Name is required') : ' '}
                        />
                    </DialogField>

                    <TemplatePicker
                        id={'digital_signing_template'}
                        options={starterOptions}
                        selectedKey={selectedKey}
                        onSelect={applyStarter}
                        blank={{key: BLANK_TEMPLATE_KEY, name: t('New template'), description: t('Start from the defaults')}}
                        disabled={data.loading || data.saving || readOnly}
                    />

                    <DialogField
                        label={t('Sectors')}
                        hint={t('Folios of reservations in these sectors can use this template. Empty: offered nowhere.')}
                    >
                        <DigitalSigningOfferField
                            value={draft.scopedSectorIds ?? []}
                            onChange={scopedSectorIds => data.setItem(previous => ({...previous, scopedSectorIds}))}
                            disabled={data.loading || readOnly}
                        />
                    </DialogField>
                </Stack>
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={safeClose} disabled={data.saving}>
                    {t('Cancel')}
                </Button>
                <LoadingButton
                    id={'btn-entity-save'}
                    variant={'contained'}
                    onClick={saveNow}
                    loading={data.saving}
                    disabled={!data.isValid || readOnly}
                >
                    {t('Create template')}
                </LoadingButton>

                {data.renderSnackbars()}
            </DialogActions>
        </Dialog>
    );
};

export default DigitalSigningTemplateCreateDialog;
